import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  PageHeader,
  Pagination,
  toast,
  type Column,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import { Eye, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { formatDate } from '@eon/utils';
import type { Group } from '@eon/api-client';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

interface GroupForm {
  name: string;
  description?: string;
}

export function GroupsPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [q, setQ] = useState('');
  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useAdminGroups({ page, pageSize, q: q.trim() || undefined, ...sortParams });
  const [editing, setEditing] = useState<Group | null>(null);
  const [creating, setCreating] = useState(false);
  const del = hooks.useDeleteGroup();

  const columns: Column<Group>[] = [
    {
      key: 'name',
      header: t('group.name'),
      sortable: true,
      cell: (g) => (
        <Link to="/groups/$groupId" params={{ groupId: g.id }} className="font-medium hover:underline">
          {g.name}
        </Link>
      ),
    },
    { key: 'desc', header: t('group.description'), sortable: true, sortKey: 'desc', cell: (g) => g.description ?? '—' },
    { key: 'members', header: t('group.members'), sortable: true, sortValue: (g) => g.memberCount, cell: (g) => g.memberCount },
    { key: 'devices', header: t('nav.devices'), sortable: true, sortValue: (g) => g.deviceCount, cell: (g) => g.deviceCount },
    { key: 'stations', header: t('nav.stations'), sortable: true, sortValue: (g) => g.stationCount, cell: (g) => g.stationCount },
    { key: 'created', header: t('user.createdAt'), sortable: true, sortValue: (g) => g.createdAt, cell: (g) => formatDate(g.createdAt) },
    {
      key: 'actions',
      header: '',
      className: 'w-36 text-right',
      cell: (g) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/groups/$groupId" params={{ groupId: g.id }}>
              <Eye className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setEditing(g)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (window.confirm(`"${g.name}"`)) {
                del.mutate(g.id, {
                  onSuccess: () => toast.success(t('actions.delete')),
                  onError: (e) => toast.error(resolveApiError(e, t)),
                });
              }
            }}
          >
            <Trash2 className="text-destructive h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('nav.groups')}
        actions={
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" /> {t('actions.create')}
              </Button>
            </DialogTrigger>
            <GroupDialog
              title={t('group.createTitle')}
              description={t('group.createDescription')}
              onClose={() => setCreating(false)}
              mode="create"
            />
          </Dialog>
        }
      />
      <div className="relative mb-4 max-w-md">
        <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
        <Input
          className="pl-9"
          placeholder={t('actions.search')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
      </div>
      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(g) => g.id}
        emptyTitle={t('group.notFound')}
        emptyDescription={t('group.emptyDescription')}
        sort={sort}
        onSortChange={onSortChange}
      />
      {list.data && (list.data.totalCount ?? 0) > 0 ? (
        <Pagination
          page={page}
          totalPages={list.data.totalPages ?? 1}
          totalCount={list.data.totalCount ?? 0}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing ? (
          <GroupDialog
            title={t('group.editTitle').replace('{{name}}', editing.name)}
            description={t('group.editDescription')}
            initial={editing}
            onClose={() => setEditing(null)}
            mode="edit"
          />
        ) : null}
      </Dialog>
    </>
  );
}

function GroupDialog({
  title,
  description,
  initial,
  mode,
  onClose,
}: {
  title: string;
  description: string;
  mode: 'create' | 'edit';
  initial?: Group;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = hooks.useCreateGroup();
  const update = hooks.useUpdateGroup();
  const { register, handleSubmit, formState } = useForm<GroupForm>({
    defaultValues: { name: initial?.name ?? '', description: initial?.description ?? '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (mode === 'create') {
        await create.mutateAsync(values);
        toast.success(t('group.created'));
      } else if (initial) {
        await update.mutateAsync({ id: initial.id, ...values });
        toast.success(t('group.updated'));
      }
      onClose();
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">{t('group.name')}</Label>
          <Input id="name" {...register('name', { required: true })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="description">{t('group.description')}</Label>
          <Input id="description" {...register('description')} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button type="submit" disabled={formState.isSubmitting}>
            {t('actions.save')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
